<script>
import { ref, inject, watch, computed } from 'vue';
import { useAppDataStore } from '../stores/appDataStore';
import Multiselect from '@vueform/multiselect';
import ApiHelper from '../helpers/apiHelper';
export default {
  components: {
    Multiselect
  },
  props: {
    formType: {
      type: String,
      required: false,
      default: '',
    },
    formId: {
      type: String,
      required: false,
      default: ''
    },
    appThemeBrand: {
      type: String,
      required: false,
      default: 'default'
    }
  },
  setup(props, { emit }) {
    const appDataStore = useAppDataStore();
    const contact = computed(() => appDataStore.appData.contact);
    const globalApiEndPoints = inject('globalApiEndPoints');
    let type = props.formType;
    let formHtmlId = props.formId;
    let validationErrors = ref([]);
    let apiUrlPostcode = globalApiEndPoints.value.apiUrlPostcode;
    let apiUrlStreet = globalApiEndPoints.value.apiUrlStreet;
    let zipCodeErrorVisible = ref(false);

    const fieldsNotToValidate = [
      'addresses',
      'city',
      'servicesID',
      'region',
      'zip',
      'postcodeKey',
      'installation_address_postcodeKey',
      'zipOptions',
      'installation_address_zipOptions',
      'meeting_times',
      'meeting_times_input',
      'meeting_times_api',
      'meeting_times_api_ts',
      'termin_select'
    ];

    const filteredAddresses = ref([])

    // Emit an event to notify the parent about changes
    const notifyParent = () => {
      emit('validation-updated', validationErrors.value);
    };
    const validateAllFields = (type = '') => {
      // Reset all errors
      validationErrors.value = [];

      // Validate each field in the contact.value object
      for (let field in contact.value) {
        // If not form type specified
        if (!type) {
          // Do not validate installation address and fields that are excluded
          if (!field.startsWith('installation_address_') && !fieldsNotToValidate.includes(field)) {
            validateContactForm(field);
          }
        }
        // If form type is specified then validate specified fields except fields that are excluded
        if (type) {
          if (!fieldsNotToValidate.includes(field) && field.startsWith(type)) {
            validateContactForm(field);
          }
        }
      }
    };

    const nameWithLang  = ({ street, streetKey, postcodeKey, postcode }) => {
      return `${street}`
    };

    const handlePostcodeChange = async (data) => {
      if (data.length < 2) {
        return;
      }

      const apiUrl = `${apiUrlPostcode}?postcode=${data}`;
      const apiHelper = new ApiHelper(apiUrl, 'GET', '');
      const zipcodeResponse = await apiHelper.fetchData();

      if (zipcodeResponse.status !== 200 && zipcodeResponse.status !== 204) {
        console.error("Fetching zipcode Data failed: ", zipcodeResponse.status);
        return;
      }

      if (zipcodeResponse.data.data.status == 404) {
        console.error("Unknown zipcode!");
        return;
      }

      // Map the response in to the format that is needed for autocomplete component
      contact.value[type + 'zipOptions'] = zipcodeResponse.data.data.map((item) => ({
        value: {
          postcodeKey: item.postcodeKey,
          postcode: item.postcode,
          place: item.place
        },
      }));

      contact.value[type + 'zipOfContact'] = data;
      zipCodeErrorVisible.value = false;
    }

    const handlePostcodeSelect = async () => {

      // Save actual request data
      contact.value[type + 'stadt'] = contact.value[type + 'zipOfContact']?.place || contact.value[type + 'zipOfContact']?.value.place;
      contact.value[type + 'street'] = '';

      let postcodeKey = contact.value[type + 'zipOfContact']?.postcodeKey || contact.value[type + 'zipOfContact']?.value.postcodeKey;

      if (postcodeKey) {
        await getStreetDataApi(postcodeKey);
      }

      zipCodeErrorVisible.value = false;

      validateContactForm(type + 'zipOfContact');
      validateContactForm(type + 'stadt');
    };

    const handlePostcodeClose = async () => {
      // If there is only one option left then autoselect it
      if (contact.value[type + 'zipOptions'].length == 1) {
        contact.value[type + 'zipOfContact'] = contact.value[type + 'zipOptions'][0];
        contact.value[type + 'stadt'] = contact.value[type + 'zipOfContact'].value.place;
        contact.value[type + 'street'] = '';

        // Fetch addresses again for the new zipcode
        const postcodeKey = contact.value[type + 'zipOfContact'].value.postcodeKey;

        await getStreetDataApi(postcodeKey);
        validateContactForm(type + 'zipOfContact');
        validateContactForm(type + 'stadt');
      }

      // If there are two options left display error message
      if (contact.value[type + 'zipOptions'].length === 2 && contact.value[type + 'zipOfContact'].length === 5) {
        zipCodeErrorVisible.value = true;
      }

    }
    const handlePostcodeClear = () => {
      contact.value[type + 'zipOfContact'] = '';
      validateContactForm(type + 'zipOfContact');
    }

    const getStreetDataApi = async (postcodeKey = null) => {
      if (!postcodeKey) {
        console.error('Error in getStreetDataApi');
        return;
      }

      const apiUrl = apiUrlStreet + '?postcodeid=' + postcodeKey;
      const apiHelper = new ApiHelper(apiUrl, 'GET', '');
      const streetDataResponse = await apiHelper.fetchData();

      if (streetDataResponse.status !== 200 && streetDataResponse.status !== 204) {
        console.error("Fetching street Data failed: ", streetDataResponse.status);
        return;
      }

      contact.value.addresses = streetDataResponse.data.map(({ street }) => street);
    }

    // Handle custom street name input
    const handleStreet = (data) => {
      // Create a seperate array for custom street name
      filteredAddresses.value.push(data);
    };

    // Handle what happens after user clicks or taps out of the street dropdown
    const handleStreetClose = (data) => {
      // Check if a custom street name was typed in
      const streetName = filteredAddresses.value?.pop();
      let customStreet = null;

      if (streetName) {
        customStreet = {
          value: streetName?.trim(),
          street: streetName?.trim(),
        };
      }

      // Assign the custom street name
      contact.value[type + 'street'] = customStreet;
      validateContactForm(type + 'street');
    };
    // Validation logic for the form input fields
    const validateContactForm = (field) => {
      let appDataContact = contact.value;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      // Fields that can only contain integers 0-9
      const numericFields = [
        type + 'phone_prefix',
        type + 'phone',
      ];
      // Validate numerics fields
      if (numericFields.includes(field)) {
        appDataContact[field] = appDataContact[field].replace(/[^0-9]/g, '');
      }

      let valid = !!appDataContact[field];

      if (field === type + 'street') {
        valid = !!appDataContact[field]?.street?.trim();
      }

      if (!valid && !validationErrors.value.includes(field)) {
        validationErrors.value.push(field);
      }

      if (valid) {
        validationErrors.value = validationErrors.value.filter(item => item !== field);
      }

      if (field === type + 'email') {
        // Validate email format using a regular expression
        if (!emailRegex.test(appDataContact[field])) {
          validationErrors.value.push(field);
          valid = false;
        }
      }

      // After updating validationErrors, notify the parent component
      notifyParent();
      return valid;
    };

    // Watcher for appData.contact.value.street
    watch(() => contact.value[type + 'street'], () => {
      // Watcher executes before the close event
      if (contact.value[type + 'street']) {
        filteredAddresses.value = [];
      }
      validateContactForm(type + 'street');
    });

    return {
      type, // Form type [ex. installation address]
      contact,
      formHtmlId,
      validateContactForm,
      handlePostcodeSelect,
      nameWithLang,
      validationErrors,
      validateAllFields,
      notifyParent,
      apiUrlPostcode,
      apiUrlStreet,
      handleStreet,
      handleStreetClose,
      handlePostcodeChange,
      handlePostcodeClose,
      handlePostcodeClear,
      zipCodeErrorVisible
    }

    // expose to template and other options API hooks
  },
}

</script>

<template>
  <form
    :id="formHtmlId"
    ref="mailform"
    class="mailform"
    name="mailform"
  >
    <div class="first-column">
      <div class="stepper__input">
        <div class="field">
          <select
            :id="type + 'mailformAnrede'"
            v-model.lazy="contact[type + 'anrede']"
            :class="{'input': true, 'is-danger': validationErrors.includes(type + 'anrede') }"
            :name="type + 'Anrede'"
            @change="validateContactForm(type + 'anrede')"
          >
            <option disabled value="">
              Bitte auswählen
            </option>
            <option value="Frau">
              Frau
            </option>
            <option value="Herr">
              Herr
            </option>
            <option value="Divers">
              Divers
            </option>
          </select>
          <span
            v-if="validationErrors.includes(type + 'anrede')"
            class="icon icon--sort-up help is-danger">Anrede ist ein Pflichtfeld.
          </span>
        </div>
      </div>
    </div>

    <div class="stepper__input">
      <div class="field">
        <input
          v-model.lazy="contact[type + 'firstname']"
          :class="{'input': true, 'is-danger': validationErrors.includes(type + 'firstname') }"
          placeholder="Vorname"
          type="text"
          name="Vorname"
          @change="validateContactForm(type + 'firstname')"
        >
        <span
          v-if="validationErrors.includes(type + 'firstname')"
          class="icon icon--sort-up help is-danger">Vorname ist ein Pflichtfeld.
        </span>
      </div>
      <div class="field">
        <input
          v-model.lazy="contact[type + 'lastname']"
          :class="{'input': true, 'is-danger': validationErrors.includes(type + 'lastname') }"
          placeholder="Nachname"
          name="Nachname"
          type="text"
          @change="validateContactForm(type + 'lastname')"
        >
        <span
          v-if="validationErrors.includes(type + 'lastname')"
          class="icon icon--sort-up help is-danger">Nachname ist ein Pflichtfeld.
        </span>
      </div>
    </div>
    <div class="stepper__input">
      <div class="field zipofcontact-field">
        <Multiselect
          v-model="contact[type + 'zipOfContact']"
          class="postcode-autocomplete"
          name="postcode-autocomplete"
          input-type="text"
          autocomplete="off"
          placeholder="PLZ"
          :searchable="true"
          :options="contact[type + 'zipOptions']"
          no-options-text="Bitte geben Sie eine Postleitzahl ein"
          :resolve-on-load="false"
          :filterResults="false"
          :object="true"
          track-by="value.postcodeKey"
          @search-change="handlePostcodeChange"
          @select="handlePostcodeSelect"
          @close="handlePostcodeClose"
          @clear="handlePostcodeClear"
        >
          <template v-slot:singlelabel="{ value }">
            <div class="multiselect-single-label">
              <span> {{ value.value.postcode }} </span>
            </div>
          </template>

          <template v-slot:option="{ option }">
            <span><strong> {{ option.value?.postcode }} </strong></span>
            <span> {{ option.value?.place }} </span>
          </template>

        </Multiselect>

        <span
          v-if="validationErrors.includes(type + 'zipOfContact')"
          class="icon icon--sort-up help is-danger">Postleitzahl ist ein Pflichtfeld.
        </span>

        <span
          v-if="zipCodeErrorVisible"
          class="icon icon--sort-up help is-danger zipcode-two-results">Zu Ihrer Postleitzahl gibt es mehr als einen Ort. Bitte wählen Sie einen Ort aus der Liste aus.
        </span>

      </div>

      <div class="field">
        <input
          v-model.lazy="contact[type + 'stadt']"
          :class="{'input': true, 'is-danger': validationErrors.includes(type + 'stadt') }"
          type="text"
          name="stadt"
          placeholder="Stadt"
          @change="validateContactForm(type + 'stadt')"
        >
        <span
          v-if="validationErrors.includes(type + 'stadt')"
          class="icon icon--sort-up help is-danger">Stadt ist ein Pflichtfeld.
        </span>
      </div>
    </div>
    <div class="stepper__input">
      <div class="field street-field">
        <multiselect
          v-model="contact[type + 'street']"
          :searchable="true"
          :options="contact.addresses"
          :object="true"
          :create-option="true"
          :custom-label="nameWithLang"
          input-type="text"
          placeholder="Straße"
          label="street"
          track-by="street"
          @search-change="handleStreet"
          @close="handleStreetClose"
        >
        </multiselect>
        <span
          v-if="validationErrors.includes(type + 'street')"
          class="icon icon--sort-up help is-danger">Straße ist ein Pflichtfeld.</span>
      </div>

      <div class="field">
        <input
          v-model.lazy="contact[type + 'house_number']"
          :class="{'input': true, 'is-danger': validationErrors.includes(type + 'house_number') }"
          name="house_number"
          type="text"
          placeholder="Hausnummer"
          @change="validateContactForm(type + 'house_number')"
        >
        <span
          v-if="validationErrors.includes(type + 'house_number')"
          class="icon icon--sort-up help is-danger">Hausnummer ist ein Pflichtfeld.</span>
      </div>
    </div>
    <div class="stepper__input">
      <div class="field">
        <input
          v-model="contact[type + 'phone_prefix']"
          :class="{'input': true, 'is-danger': validationErrors.includes(type + 'phone_prefix') }"
          name="phone_prefix"
          type="text"
          placeholder="Vorwahl"
          maxlength="6"
          @input="validateContactForm(type + 'phone_prefix')"
        >
        <span
          v-if="validationErrors.includes(type + 'phone_prefix')"
          class="icon icon--sort-up help is-danger">Vorwahl ist ein Pflichtfeld.
        </span>
      </div>

      <div class="field">
        <input
          v-model="contact[type + 'phone']"
          :class="{'input': true, 'is-danger': validationErrors.includes(type + 'phone') }"
          name="Telefon"
          type="text"
          pattern="[0-9]*"
          placeholder="Telefonnummer"
          @input="validateContactForm(type + 'phone')"
        >
        <span
          v-if="validationErrors.includes(type + 'phone')"
          class="icon icon--sort-up help is-danger">Telefon ist ein Pflichtfeld.
        </span>
      </div>
    </div>
    <div class="stepper__input">
      <div class="field">
        <input
          v-model.lazy="contact[type + 'email']"
          :class="{'input': true, 'is-danger': validationErrors.includes(type + 'email') }"
          name="Email"
          type="text"
          placeholder="Email"
          @change="validateContactForm(type + 'email')"
        >
        <span
          v-if="validationErrors.includes(type + 'email')"
          class="help is-danger icon icon--sort-up">Email ist ein Pflichtfeld.
        </span>
      </div>
    </div>
  </form>
</template>

<style lang="scss">

</style>
